import { useDrag, UserDragConfig } from '@use-gesture/react'
import defer from 'lodash/defer'
import { RefObject, useContext, useRef } from 'react'

import { ControllerContext } from '@Components/Product/Slider/Context/ControllerContext'

interface DragMemo {
  mx: any
  originalTransition: string
  originalTranslateX: string
}

const DRAG_SCREEN_THRESHOLD = 0.2

export function DragNext<T extends HTMLElement>(
  target: RefObject<T | null>,
  goToNextSlide: (recalculate: boolean) => void,
  goToPreviousSlide: (recalculate: boolean) => void,
  activeSlide: number,
  axis?: UserDragConfig['axis'],
  config = {
    dragBounds: true,
    threshold: DRAG_SCREEN_THRESHOLD,
  },
) {
  const isDragging = useRef<boolean>(false)
  const { isZoomedIn } = useContext(ControllerContext)

  useDrag(
    ({
      first,
      dragging,
      last,
      movement: [mx],
      xy: [x, y],
      memo = {},
      tap,
      cancel,
    }) => {
      if (isZoomedIn || tap) {
        return memo
      }
      const next: DragMemo = {
        originalTransition: memo?.originalTransition || '',
        originalTranslateX: memo?.originalTranslateX || '',
        mx,
      }
      if (!target?.current) {
        cancel()
        return memo
      }

      if (first) {
        isDragging.current = !!dragging
        const result = target.current.style.transform.match(/translateX\((.*?)\)/)
        next.originalTranslateX = result && result.length > 1 ? result[1] : '0%'
        next.originalTransition = target.current.style.transition
        // eslint-disable-next-line no-param-reassign
        target.current.style.transition = ''
      }

      const bounds = target.current.parentElement?.getBoundingClientRect()
      if (!bounds) {
        return next
      }

      if (config.dragBounds && (x < bounds.left || x > bounds.right || y < bounds.top || y > bounds.bottom)) {
        cancel()
      }

      if (!last) {
        // eslint-disable-next-line no-param-reassign
        target.current.style.transform = `translateX(calc(${next.originalTranslateX} + ${mx}px))`
        return next
      }

      if (last) {
        // eslint-disable-next-line no-param-reassign
        target.current.style.transition = 'transform .45s cubic-bezier(.215,.61,.355,1)'
        if (memo.mx > (window.innerWidth * config.threshold)) {
          if (activeSlide === 0) {
            // eslint-disable-next-line no-param-reassign
            target.current.style.transition = ''
          }
          goToPreviousSlide(true)
        } else if (memo.mx < -(window.innerWidth * config.threshold)) {
          if (activeSlide === target.current.childElementCount - 1) {
            // eslint-disable-next-line no-param-reassign
            target.current.style.transition = ''
          }
          goToNextSlide(true)
        } else {
          // eslint-disable-next-line no-param-reassign
          target.current.style.transform = `translateX(${next.originalTranslateX})`
        }
        defer(() => {
          isDragging.current = !!dragging
        })
      }

      return next
    },
    {
      axis,
      swipe: { velocity: 10000000000000000 },
      axisThreshold: { pen: 16, mouse: 10, touch: 10 },
      target,
      preventScrollAxis: 'y',
      filterTaps: false,
    },
  )
  return { isDragging }
}
